import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Race, Runner } from '../types';
import { formatTime, formatDate, formatRaceSpecs, getConfidenceColor, formatOdds, isNonRunner } from '../utils/helpers';
import { Check, X, Clock, Ban, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { fetchProfile } from '../services/profileService';
import { createBet, createOrReuseUnmatchedBetfairBet, fetchBetByRunner, updateBet, deleteBet, updateBetStatus, updateBetWithBetfairId, UserBet } from '../services/betService';
import { getBetfairOdds, placeBetfairBet } from '../services/betfairService';
import CircularProgress from './CircularProgress';
import RunnerComparisonTable from './RunnerComparisonTable';
import './RaceCard.css';

// Helper function to determine result status
const getResultStatus = (raceResult: string | null, aiSelection: string, nonRunner?: string): string => {
  if (!raceResult) return 'PENDING';
  
  // Check if it's a non-runner first
  if (isNonRunner(aiSelection, nonRunner)) return 'VOID';
  
  // Check if the race result matches the AI selection (ignoring brackets)
  const normalizeName = (name: string) => name.replace(/\s*\([^)]*\)\s*$/, '').trim();
  return normalizeName(raceResult) === normalizeName(aiSelection) ? 'WON' : 'LOST';
};

// Helper function to get result icon
const getResultIcon = (raceResult: string | null, aiSelection: string, nonRunner?: string): JSX.Element => {
  const status = getResultStatus(raceResult, aiSelection, nonRunner);
  switch (status) {
    case 'WON': return <Check size={16} />;
    case 'LOST': return <X size={16} />;
    case 'VOID': return <Ban size={16} />;
    case 'PENDING': return <Clock size={16} />;
    default: return <Clock size={16} />;
  }
};

interface RaceCardProps {
  race: Race;
  runners: Runner[];
  allCollapsed?: boolean;
}

const RaceCard: React.FC<RaceCardProps> = ({ race, runners, allCollapsed = false }) => {
  // Version 2.0 - Mobile tables fix
  const { user, session } = useAuth();
  const [showRunners, setShowRunners] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogBetModal, setShowLogBetModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stake, setStake] = useState('');
  const [odds, setOdds] = useState('');
  const [bookmaker, setBookmaker] = useState('');
  const [notes, setNotes] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [logging, setLogging] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingBet, setExistingBet] = useState<UserBet | null>(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [lastActionWasUpdate, setLastActionWasUpdate] = useState(false);
  
  // Betfair bet dialog state
  const [showBetfairBetModal, setShowBetfairBetModal] = useState(false);
  const [betfairStake, setBetfairStake] = useState('');
  const [betfairOdds, setBetfairOdds] = useState('');
  const [betfairNotes, setBetfairNotes] = useState('');
  const [loadingBetfairOdds, setLoadingBetfairOdds] = useState(false);
  const [betfairError, setBetfairError] = useState<string | null>(null);
  const [placingBetfairBet, setPlacingBetfairBet] = useState(false);
  const [betfairRetryAttempt, setBetfairRetryAttempt] = useState(0);
  const [isViewingBetfairBet, setIsViewingBetfairBet] = useState(false);
  const [showDisabledMessage, setShowDisabledMessage] = useState(false);
  const [defaultStake, setDefaultStake] = useState<number | null>(null);

  const handleToggleRunners = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRunners(!showRunners);
  };

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCollapsed(!isCollapsed);
  };

  // Sync local collapsed state with allCollapsed prop
  useEffect(() => {
    setIsCollapsed(allCollapsed);
  }, [allCollapsed]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Filter runners for this race
  const raceRunners = runners.filter(runner => runner.race_id === race.race_id);

  const normalizeName = (name: string) => name.replace(/\s*\([^)]*\)\s*$/, '').trim();

  const normalizedAISelection = normalizeName(race.ai_selection);

  const validRunnersWithOdds = raceRunners
    .map((runner) => {
      const odds = parseFloat(runner.bet365_decimal_odds || '');
      return Number.isFinite(odds) && odds > 0 ? { runner, odds } : null;
    })
    .filter((x): x is { runner: Runner; odds: number } => x !== null);

  const favouriteOdds =
    validRunnersWithOdds.length > 0
      ? Math.min(...validRunnersWithOdds.map((x) => x.odds))
      : null;

  const favouriteRunners =
    favouriteOdds === null
      ? []
      : validRunnersWithOdds
          .filter((x) => x.odds === favouriteOdds)
          .map((x) => x.runner);

  const isAISelectionFavourite =
    favouriteRunners.length > 0 &&
    favouriteRunners.some((r) => normalizeName(r.horse_name) === normalizedAISelection);

  const isJointFavourite = isAISelectionFavourite && favouriteRunners.length > 1;

  const favouriteLabel = isAISelectionFavourite ? (isJointFavourite ? 'Joint favourite' : 'Favourite') : null;

  // Find the selected runner
  const selectedRunner = raceRunners.find(runner => {
    const runnerName = normalizeName(runner.horse_name);
    return runnerName === normalizedAISelection;
  });

  // Check if bet exists for this runner
  useEffect(() => {
    const checkExistingBet = async () => {
      if (user?.id && selectedRunner) {
        try {
          const bet = await fetchBetByRunner(user.id, selectedRunner.runner_id);
          setExistingBet(bet);
        } catch (error) {
          console.error('Error checking existing bet:', error);
          setExistingBet(null);
        }
      } else {
        setExistingBet(null);
      }
    };
    checkExistingBet();
  }, [user?.id, selectedRunner]);

  // Also check bet when component first renders (in case RLS was just set up)
  useEffect(() => {
    if (user?.id && selectedRunner && !existingBet) {
      const timer = setTimeout(async () => {
        try {
          const bet = await fetchBetByRunner(user.id, selectedRunner.runner_id);
          if (bet) {
            setExistingBet(bet);
          }
        } catch (error) {
          // Silently fail - bet might not exist
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user?.id, selectedRunner, existingBet]);

  // Refresh bet check when confirmation modal closes
  useEffect(() => {
    if (!showConfirmation && user?.id && selectedRunner && !showLogBetModal) {
      const refreshBet = async () => {
        try {
          const bet = await fetchBetByRunner(user.id, selectedRunner.runner_id);
          setExistingBet(bet);
        } catch (error) {
          console.error('Error refreshing bet after confirmation:', error);
        }
      };
      // Small delay to ensure modal is fully closed
      const timer = setTimeout(() => {
        refreshBet();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showConfirmation, user?.id, selectedRunner, showLogBetModal]);

  // Fetch user currency and default stake on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (user?.id) {
        try {
          const profile = await fetchProfile(user.id);
          if (profile?.currency) {
            setCurrency(profile.currency);
          }
          if (profile?.default_stake !== null && profile?.default_stake !== undefined) {
            setDefaultStake(profile.default_stake);
          }
        } catch (error) {
          console.error('Error loading profile:', error);
        }
      }
    };
    loadProfile();
  }, [user]);

  // Initialize odds with race odds when modal opens
  useEffect(() => {
    if (showLogBetModal && race.bet365_decimal_odds) {
      const raceOdds = typeof race.bet365_decimal_odds === 'string' 
        ? parseFloat(race.bet365_decimal_odds) 
        : race.bet365_decimal_odds;
      setOdds(raceOdds.toFixed(2));
    }
  }, [showLogBetModal, race.bet365_decimal_odds]);

  // Calculate potential profit
  const calculatePotentialProfit = (): number => {
    const stakeNum = parseFloat(stake);
    const oddsNum = parseFloat(odds);
    if (isNaN(stakeNum) || isNaN(oddsNum) || stakeNum <= 0 || oddsNum <= 0) {
      return 0;
    }
    return stakeNum * (oddsNum - 1);
  };

  const handleDisabledButtonTap = (e: React.TouchEvent | React.MouseEvent) => {
    if (raceHasStarted) {
      e.preventDefault();
      e.stopPropagation();
      // Show styled notification on mobile
      setShowDisabledMessage(true);
      setTimeout(() => {
        setShowDisabledMessage(false);
      }, 3000); // Hide after 3 seconds
    }
  };

  // Helper function to calculate suggested stake
  const calculateSuggestedStake = (): string => {
    if (defaultStake === null || defaultStake === undefined || !race.suggested_stake) {
      return '';
    }
    const suggestedStakePoints = parseFloat(race.suggested_stake);
    if (isNaN(suggestedStakePoints)) {
      return '';
    }
    const calculatedStake = defaultStake * suggestedStakePoints;
    return calculatedStake.toFixed(2);
  };

  const handleLogBetClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't proceed if race has started
    if (raceHasStarted) {
      return;
    }
    
    // Always refresh bet check before opening modal to ensure we have latest state
    if (user?.id && selectedRunner) {
      try {
        const bet = await fetchBetByRunner(user.id, selectedRunner.runner_id);
        setExistingBet(bet);
        
        if (bet) {
          // Update mode - populate with existing bet data
          setIsUpdateMode(true);
          setStake(bet.stake.toString());
          setOdds(bet.odds.toString());
          setBookmaker(bet.bookmaker);
          setNotes(bet.notes || '');
        } else {
          // New bet mode - pre-populate with calculated stake
          setIsUpdateMode(false);
          const suggestedStake = calculateSuggestedStake();
          setStake(suggestedStake);
          setBookmaker('');
          setNotes('');
        }
      } catch (error) {
        console.error('Error refreshing bet on click:', error);
        setIsUpdateMode(false);
        const suggestedStake = calculateSuggestedStake();
        setStake(suggestedStake);
        setBookmaker('');
        setNotes('');
      }
    } else {
      // No user/runner, but still calculate suggested stake
      const suggestedStake = calculateSuggestedStake();
      setStake(suggestedStake);
    }
    
    setError(null);
    setShowLogBetModal(true);
  };

  const handleLogBetClose = async () => {
    setShowLogBetModal(false);
    setShowConfirmation(false);
    setShowDeleteConfirm(false);
    setStake('');
    setOdds('');
    setBookmaker('');
    setNotes('');
    setError(null);
    setIsUpdateMode(false);
    
    // Refresh bet check to ensure button state is up to date
    if (user?.id && selectedRunner) {
      try {
        const bet = await fetchBetByRunner(user.id, selectedRunner.runner_id);
        setExistingBet(bet);
      } catch (error) {
        console.error('Error refreshing bet check:', error);
      }
    }
  };

  // Check if there's an existing Betfair bet
  const hasBetfairBet = existingBet?.betfair_bet_id !== null && existingBet?.betfair_bet_id !== undefined;

  // Check if the race has started
  const [raceHasStarted, setRaceHasStarted] = useState(false);

  useEffect(() => {
    const checkRaceStart = () => {
      try {
        const now = new Date();
        
        // Parse race date and time
        // race_date format: "YYYY-MM-DD" (e.g., "2025-10-06")
        // race_time format: "HH:MM:SS" (e.g., "09:00:00") or "HH:MM"
        const raceDateStr = race.race_date; // "2025-10-06"
        const raceTimeStr = race.race_time; // "09:00:00" or "09:00"
        
        // Ensure time has seconds if not present
        const timeParts = raceTimeStr.split(':');
        const formattedTime = timeParts.length === 2 
          ? `${raceTimeStr}:00` 
          : raceTimeStr;
        
        // Create ISO string: "2025-10-06T09:00:00"
        // Note: This will be interpreted as local time
        const raceDateTimeStr = `${raceDateStr}T${formattedTime}`;
        const raceDateTime = new Date(raceDateTimeStr);
        
        // Check if date is valid
        if (isNaN(raceDateTime.getTime())) {
          console.error('Invalid race date/time:', raceDateStr, raceTimeStr);
          setRaceHasStarted(false);
          return;
        }
        
        const hasStarted = now >= raceDateTime;
        setRaceHasStarted(hasStarted);
      } catch (error) {
        console.error('Error checking race start time:', error);
        setRaceHasStarted(false);
      }
    };

    // Check immediately
    checkRaceStart();
    
    // Recheck every minute to update disabled state
    const interval = setInterval(checkRaceStart, 60000);
    
    return () => clearInterval(interval);
  }, [race.race_id, race.race_date, race.race_time]);

  // Betfair bet dialog handlers
  const handleBetfairBetClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't proceed if race has started
    if (raceHasStarted) {
      return;
    }
    
    // If viewing an existing bet, just show the details
    if (hasBetfairBet && existingBet) {
      setIsViewingBetfairBet(true);
      setBetfairStake(existingBet.stake.toString());
      setBetfairOdds(existingBet.odds.toString());
      setBetfairNotes(existingBet.notes || '');
      setBetfairError(null);
      setShowBetfairBetModal(true);
      return;
    }
    
    // Otherwise, place a new bet
    if (!race.betfair_market_id || !race.betfair_selection_id) {
      setBetfairError('This race is not yet mapped to Betfair. Please wait for the mapping job to complete.');
      setShowBetfairBetModal(true);
      setIsViewingBetfairBet(false);
      return;
    }

    if (!user?.id || !session?.access_token) {
      setBetfairError('You must be logged in to place a bet on Betfair');
      setShowBetfairBetModal(true);
      setIsViewingBetfairBet(false);
      return;
    }

    setIsViewingBetfairBet(false);
    setBetfairError(null);
    setLoadingBetfairOdds(true);
    setShowBetfairBetModal(true);
    // Pre-populate stake with calculated value
    const suggestedStake = calculateSuggestedStake();
    setBetfairStake(suggestedStake);
    setBetfairNotes('');

    try {
      const oddsData = await getBetfairOdds(
        race.betfair_market_id,
        race.betfair_selection_id,
        session.access_token
      );

      if (oddsData.bestBackPrice) {
        setBetfairOdds(oddsData.bestBackPrice.toFixed(2));
      } else if (oddsData.lastPriceTraded) {
        setBetfairOdds(oddsData.lastPriceTraded.toFixed(2));
      } else {
        setBetfairError('No odds available for this selection');
      }
    } catch (error: any) {
      console.error('Error fetching Betfair odds:', error);
      setBetfairError(error.message || 'Failed to fetch Betfair odds');
    } finally {
      setLoadingBetfairOdds(false);
    }
  };

  const handleBetfairBetClose = (e?: React.MouseEvent) => {
    // Only close if clicking directly on overlay, not if event is from modal content
    if (e && e.target !== e.currentTarget) {
      return;
    }
    setShowBetfairBetModal(false);
    setBetfairStake('');
    setBetfairOdds('');
    setBetfairNotes('');
    setBetfairError(null);
    setBetfairRetryAttempt(0);
    setIsViewingBetfairBet(false);
  };

  const handleBetfairBetSubmit = async () => {
    // Validation
    if (!betfairStake || !betfairOdds) {
      setBetfairError('Please fill in Stake and Odds');
      return;
    }

    const stakeNum = parseFloat(betfairStake);
    const oddsNum = parseFloat(betfairOdds);

    if (isNaN(stakeNum) || stakeNum <= 0) {
      setBetfairError('Stake must be a positive number');
      return;
    }

    if (isNaN(oddsNum) || oddsNum <= 0) {
      setBetfairError('Odds must be a positive number');
      return;
    }

    if (!user?.id || !session?.access_token) {
      setBetfairError('You must be logged in to place a bet');
      return;
    }

    if (!selectedRunner) {
      setBetfairError('Could not find the selected runner');
      return;
    }

    if (!race.betfair_market_id || !race.betfair_selection_id) {
      setBetfairError('Betfair mapping not available for this race');
      return;
    }

    setPlacingBetfairBet(true);
    setBetfairError(null);

    // First, create the bet in our database
    let betRecord: UserBet;
    try {
      betRecord = await createOrReuseUnmatchedBetfairBet({
        user_id: user.id,
        runner_id: selectedRunner.runner_id,
        bookmaker: 'Betfair Exchange',
        stake: stakeNum,
        odds: oddsNum,
        notes: betfairNotes || null,
        placed_by_ai: false,
      });
    } catch (error: any) {
      console.error('Error creating bet record:', error);
      setBetfairError('Failed to create bet record: ' + (error.message || 'Unknown error'));
      setPlacingBetfairBet(false);
      return;
    }

    // Try to place bet on Betfair with retry logic (up to 3 attempts)
    const maxRetries = 3;
    let lastError: string | null = null;
    let lastOdds = oddsNum;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      setBetfairRetryAttempt(attempt);
      
      try {
        // If this is a retry, refresh odds first
        if (attempt > 1) {
          setBetfairError(`Attempt ${attempt} of ${maxRetries}: Refreshing odds and retrying...`);
          setLoadingBetfairOdds(true);
          try {
            const oddsData = await getBetfairOdds(
              race.betfair_market_id,
              race.betfair_selection_id,
              session.access_token
            );

            if (oddsData.bestBackPrice) {
              lastOdds = oddsData.bestBackPrice;
              setBetfairOdds(lastOdds.toFixed(2));
            } else if (oddsData.lastPriceTraded) {
              lastOdds = oddsData.lastPriceTraded;
              setBetfairOdds(lastOdds.toFixed(2));
            }
          } catch (oddsError) {
            console.error('Error refreshing odds on retry:', oddsError);
          } finally {
            setLoadingBetfairOdds(false);
          }
        }

        // Place bet on Betfair
        const result = await placeBetfairBet(
          user.id,
          race.betfair_market_id,
          race.betfair_selection_id,
          stakeNum,
          lastOdds, // Use refreshed odds if available
          session.access_token
        );

        if (result.success && result.status === 'matched' && result.betId) {
          // Bet matched successfully - update bet record with Betfair bet ID
          await updateBetWithBetfairId(
            betRecord.id,
            result.betId,
            result.averagePriceMatched
          );

          // Close dialog and refresh
          setBetfairRetryAttempt(0);
          handleBetfairBetClose();
          
          // Refresh bet check to update button text
          if (user?.id && selectedRunner) {
            try {
              const bet = await fetchBetByRunner(user.id, selectedRunner.runner_id);
              setExistingBet(bet);
            } catch (error) {
              console.error('Error refreshing bet check:', error);
            }
          }

          setPlacingBetfairBet(false);
          return; // Success - exit function
        } else if (result.status === 'unmatched') {
          // Bet unmatched - will retry if attempts remain
          lastError = result.error || result.message || 'Bet could not be matched';
          
          if (attempt < maxRetries) {
            // Wait a bit before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          } else {
            // All retries exhausted
            setBetfairError(
              `Bet could not be matched after ${maxRetries} attempts. ${lastError}. Odds have been refreshed - please try again.`
            );
            
            // Refresh odds for user to try again
            setLoadingBetfairOdds(true);
            try {
              const oddsData = await getBetfairOdds(
                race.betfair_market_id,
                race.betfair_selection_id,
                session.access_token
              );

              if (oddsData.bestBackPrice) {
                setBetfairOdds(oddsData.bestBackPrice.toFixed(2));
              } else if (oddsData.lastPriceTraded) {
                setBetfairOdds(oddsData.lastPriceTraded.toFixed(2));
              }
            } catch (oddsError) {
              console.error('Error refreshing odds:', oddsError);
            } finally {
              setLoadingBetfairOdds(false);
            }

            // Update bet status to unmatched
            try {
              await updateBetStatus(betRecord.id, 'unmatched');
            } catch (updateError) {
              console.error('Error updating bet status to unmatched:', updateError);
            }
          }
        } else {
          // Other failure
          lastError = result.error || 'Bet placement failed';
          setBetfairError(lastError);
          
          // Update bet status to unmatched
          try {
            await updateBetStatus(betRecord.id, 'unmatched');
          } catch (updateError) {
            console.error('Error updating bet status:', updateError);
          }
          
          break; // Don't retry on non-unmatched failures
        }
      } catch (error: any) {
        console.error(`Error placing Betfair bet (attempt ${attempt}):`, error);
        lastError = error.message || 'Failed to place bet on Betfair';
        
        if (attempt < maxRetries) {
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        } else {
          setBetfairError(`Failed to place bet after ${maxRetries} attempts: ${lastError}`);
          
          // Update bet status to unmatched
          try {
            await updateBetStatus(betRecord.id, 'unmatched');
          } catch (updateError) {
            console.error('Error updating bet status:', updateError);
          }
        }
      }
    }

    setPlacingBetfairBet(false);
    setBetfairRetryAttempt(0);
  };

  // Calculate potential profit for Betfair bet
  const calculateBetfairPotentialProfit = (): number => {
    const stakeNum = parseFloat(betfairStake);
    const oddsNum = parseFloat(betfairOdds);
    if (isNaN(stakeNum) || isNaN(oddsNum) || stakeNum <= 0 || oddsNum <= 0) {
      return 0;
    }
    return stakeNum * (oddsNum - 1);
  };

  // Get currency symbol helper
  const getCurrencySymbol = (curr: string): string => {
    const symbols: { [key: string]: string } = {
      'GBP': '£',
      'USD': '$',
      'EUR': '€',
      'AUD': 'A$',
      'CAD': 'C$',
    };
    return symbols[curr] || curr;
  };

  const handleLogBetSubmit = async () => {
    // Validation
    if (!stake || !odds || !bookmaker) {
      setError('Please fill in all required fields (Stake, Odds, and Bookmaker)');
      return;
    }

    const stakeNum = parseFloat(stake);
    const oddsNum = parseFloat(odds);

    if (isNaN(stakeNum) || stakeNum <= 0) {
      setError('Stake must be a positive number');
      return;
    }

    if (isNaN(oddsNum) || oddsNum <= 0) {
      setError('Odds must be a positive number');
      return;
    }

    if (!user?.id) {
      setError('You must be logged in to log a bet');
      return;
    }

    if (!selectedRunner) {
      setError('Could not find the selected runner');
      return;
    }

    if (isUpdateMode && existingBet) {
      // Update existing bet
      setUpdating(true);
      setError(null);

      try {
        const updatedBet = await updateBet(existingBet.id, {
          bookmaker: bookmaker,
          stake: stakeNum,
          odds: oddsNum,
          notes: notes.trim() || null,
        });

        setExistingBet(updatedBet);
        setLastActionWasUpdate(true);
        setShowLogBetModal(false);
        setShowConfirmation(true);
      } catch (err: any) {
        console.error('Error updating bet:', err);
        setError(err.message || 'Failed to update bet. Please try again.');
      } finally {
        setUpdating(false);
      }
    } else {
      // Create new bet
      setLogging(true);
      setError(null);

      try {
        const newBet = await createBet({
          user_id: user.id,
          runner_id: selectedRunner.runner_id,
          bookmaker: bookmaker,
          stake: stakeNum,
          odds: oddsNum,
          notes: notes.trim() || null,
          placed_by_ai: false,
        });

        // Immediately update the existing bet state so button changes
        // This should trigger a re-render and change button from "Log Bet" to "Update Bet"
        setExistingBet(newBet);
        setLastActionWasUpdate(false);
        setShowLogBetModal(false);
        setShowConfirmation(true);
        
        // Force a state update to ensure re-render
        // The button should immediately show "Update Bet" since existingBet is now set
      } catch (err: any) {
        console.error('Error logging bet:', err);
        setError(err.message || 'Failed to log bet. Please try again.');
      } finally {
        setLogging(false);
      }
    }
  };

  const handleDeleteBet = async () => {
    if (!existingBet) return;

    setDeleting(true);
    setError(null);

    try {
      await deleteBet(existingBet.id);
      setExistingBet(null);
      setShowDeleteConfirm(false);
      setShowLogBetModal(false);
      // Button will automatically show "Log Bet" since existingBet is now null
    } catch (err: any) {
      console.error('Error deleting bet:', err);
      setError(err.message || 'Failed to delete bet. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Bookmaker options by region
  const bookmakers = {
    'UK': [
      'Bet365',
      'William Hill',
      'Ladbrokes',
      'Coral',
      'Paddy Power',
      'Betfair',
      'Sky Bet',
      'Betway',
      'Unibet',
      'BetVictor',
      '888sport',
      'Betfred',
      'Boylesports',
      'Marathon Bet',
      'Betfair Exchange'
    ],
    'US': [
      'DraftKings',
      'FanDuel',
      'BetMGM',
      'Caesars Sportsbook',
      'PointsBet',
      'BetRivers',
      'Unibet (US)',
      'Barstool Sportsbook',
      'WynnBET',
      'FOX Bet'
    ],
    'Australia': [
      'Sportsbet',
      'TAB',
      'Bet365 (AU)',
      'Unibet (AU)',
      'Ladbrokes (AU)',
      'Neds',
      'BetEasy',
      'PointsBet (AU)',
      'Betfair (AU)',
      'Palmerbet'
    ],
    'Canada': [
      'Bet365 (CA)',
      'TheScore Bet',
      'BetMGM (CA)',
      'FanDuel (CA)',
      'PointsBet (CA)',
      'BetRivers (CA)',
      'Unibet (CA)',
      '888sport (CA)'
    ]
  };

  // Mobile layout
  if (isMobile) {
    return (
      <div className="race-card mobile-race-card">
        {/* Mobile Collapsed Header */}
        <div className="mobile-race-header">
          <div className="mobile-header-row">
            <div className="mobile-header-left">
              <span className={`mobile-result-icon ${getResultStatus(race.race_result, race.ai_selection, race.non_runner).toLowerCase()}`}>
                {getResultIcon(race.race_result, race.ai_selection, race.non_runner)}
              </span>
              <span className="mobile-time-course">{formatTime(race.race_time)} {race.course}</span>
            </div>
            <div className="mobile-header-right">
              <span className="mobile-runners">{race.field_size}r</span>
              <span
                className="mobile-selection-badge"
                data-tooltip={favouriteLabel || undefined}
              >
                {isAISelectionFavourite && (
                  <span
                    className="favourite-indicator"
                    aria-label={favouriteLabel || undefined}
                  >
                    <Crown size={12} />
                  </span>
                )}
                <span className="selection-text">{normalizedAISelection}</span>
              </span>
              <button 
                className="mobile-expand-btn"
                onClick={handleToggleCollapse}
              >
                {isCollapsed ? '▼' : '▲'}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Expanded Content */}
        {!isCollapsed && (
          <div className="mobile-race-content">
            {/* AI Selection Badge */}
            <div className="mobile-ai-selection-badge">
              {race.ai_selection.replace(/\s*\([^)]*\)\s*$/, '').trim()}
            </div>

            {/* Race Specs Badge */}
            <div className="mobile-race-specs">
              {formatRaceSpecs(race)}
            </div>

            {/* FRESH TABLE 1 */}
            <div className="mobile-table-container">
              <table className="mobile-simple-table">
                <thead>
                  <tr>
                    <th>Result</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{getResultStatus(race.race_result, race.ai_selection, race.non_runner)}</td>
                    <td style={{ color: getConfidenceColor(race.ai_confidence) }}>{race.ai_confidence.toUpperCase()}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* FRESH TABLE 2 */}
            <div className="mobile-table-container">
              <table className="mobile-simple-table">
                <thead>
                  <tr>
                    <th>Stake (pts)</th>
                    <th>Odds</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{race.suggested_stake || 'N/A'}</td>
                    <td>{race.bet365_decimal_odds ? formatOdds(typeof race.bet365_decimal_odds === 'string' ? parseFloat(race.bet365_decimal_odds) : race.bet365_decimal_odds) : 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* AI Scores Table */}
            <div className="mobile-scores-table">
              <table className="mobile-table">
                <thead>
                  <tr>
                    <th>Overall</th>
                    <th>Horse</th>
                    <th>Jockey</th>
                    <th>Trainer</th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="mobile-score-cell">
                      <CircularProgress 
                        score={race.ai_overall_score} 
                        label="" 
                        size={40}
                        strokeWidth={3}
                      />
                    </td>
                    <td className="mobile-score-cell">
                      <CircularProgress 
                        score={race.ai_horse_score} 
                        label="" 
                        size={40}
                        strokeWidth={3}
                      />
                    </td>
                    <td className="mobile-score-cell">
                      <CircularProgress 
                        score={race.ai_jockey_score} 
                        label="" 
                        size={40}
                        strokeWidth={3}
                      />
                    </td>
                    <td className="mobile-score-cell">
                      <CircularProgress 
                        score={race.ai_trainer_score} 
                        label="" 
                        size={40}
                        strokeWidth={3}
                      />
                    </td>
                    <td className="mobile-score-cell">
                      <CircularProgress 
                        score={race.ai_owner_score} 
                        label="" 
                        size={40}
                        strokeWidth={3}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* AI Analysis Card */}
            <div className="mobile-ai-analysis-card">
              <div className="mobile-ai-analysis-header">
                <h4 className="mobile-ai-analysis-title">AI Analysis</h4>
              </div>
              <div className="mobile-ai-analysis-content">
                <p>{race.ai_analysis}</p>
              </div>
            </div>

            {/* Runners Section */}
            <div className="mobile-runners-section">
              <button 
                className="mobile-runners-toggle"
                onClick={handleToggleRunners}
              >
                {showRunners ? 'Hide Runners' : `View Runners (${raceRunners.length})`}
                <span className="mobile-runners-toggle-icon">
                  {showRunners ? '−' : '+'}
                </span>
              </button>
              
              {showRunners && (
                <div className="mobile-runners-content">
                  <RunnerComparisonTable runners={raceRunners} aiSelection={race.ai_selection} />
                </div>
              )}
            </div>

            {/* Bet Buttons - Mobile Expanded */}
            <div className="race-bet-buttons mobile-bet-buttons">
              {!hasBetfairBet && (
              <button 
                  type="button"
                className="race-bet-button log-bet-button"
                onClick={handleLogBetClick}
                  onTouchStart={handleDisabledButtonTap}
                  disabled={raceHasStarted}
                  title={raceHasStarted ? 'Cannot place bets as the race has begun' : ''}
              >
                {existingBet ? 'Update Bet' : 'Log Bet'}
              </button>
              )}
              {!(raceHasStarted && hasBetfairBet) && (
                <button 
                  type="button"
                  className="race-bet-button place-betfair-button"
                  onClick={handleBetfairBetClick}
                  onTouchStart={handleDisabledButtonTap}
                  disabled={raceHasStarted && !hasBetfairBet}
                  title={raceHasStarted && !hasBetfairBet ? 'Cannot place bets as the race has begun' : ''}
                >
                  {hasBetfairBet ? 'View Bet Details' : 'Place Bet on Betfair'}
              </button>
              )}
            </div>
          </div>
        )}

        {/* Bet Buttons - Mobile Collapsed */}
        {isCollapsed && (
          <div className="race-bet-buttons mobile-bet-buttons">
            {!hasBetfairBet && (
            <button 
                type="button"
              className="race-bet-button log-bet-button"
              onClick={handleLogBetClick}
                onTouchStart={handleDisabledButtonTap}
                disabled={raceHasStarted}
                title={raceHasStarted ? 'Cannot place bets as the race has begun' : ''}
            >
              {existingBet ? 'Update Bet' : 'Log Bet'}
            </button>
            )}
            {!(raceHasStarted && hasBetfairBet) && (
              <button 
                type="button"
                className="race-bet-button place-betfair-button"
                onClick={handleBetfairBetClick}
                onTouchStart={handleDisabledButtonTap}
                disabled={raceHasStarted && !hasBetfairBet}
                title={raceHasStarted && !hasBetfairBet ? 'Cannot place bets as the race has begun' : ''}
              >
                {hasBetfairBet ? 'View Bet Details' : 'Place Bet on Betfair'}
            </button>
            )}
          </div>
        )}

        {/* Disabled Button Notification - Mobile */}
        {showDisabledMessage && createPortal(
          <div className="race-card-disabled-notification">
            <div className="race-card-disabled-notification-content">
              <span>Cannot place bets as the race has begun</span>
            </div>
          </div>,
          document.body
        )}

        {/* Log Bet Modal - Mobile - Using Portal */}
        {showLogBetModal && createPortal(
          <div className="log-bet-modal-overlay" onClick={handleLogBetClose}>
            <div className="log-bet-modal" onClick={(e) => e.stopPropagation()}>
              {/* Race Information Header */}
              <div className="log-bet-modal-header">
                <div className="log-bet-race-info">
                  <div className="log-bet-race-time-course">
                    {formatTime(race.race_time)} {race.course}
                  </div>
                  <div className="log-bet-race-specs">
                    {formatRaceSpecs(race)}
                  </div>
                </div>
              </div>

              {/* Selection Badge */}
              <div className="log-bet-selection-badge-container">
                <div className="log-bet-selection-badge">
                  {race.ai_selection.replace(/\s*\([^)]*\)\s*$/, '').trim()}
                </div>
              </div>

              {/* Divider */}
              <div className="log-bet-divider"></div>

              {/* Bet Details */}
              <div className="log-bet-modal-body">
                {error && (
                  <div className="log-bet-error-message">
                    {error}
                  </div>
                )}

                <div className="log-bet-form-grid">
                  {/* Row 1: Stake | Odds */}
                  <div className="log-bet-form-group">
                    <label htmlFor="log-bet-stake-mobile">Stake ({getCurrencySymbol(currency)}) *</label>
                    <input
                      type="text"
                      id="log-bet-stake-mobile"
                      value={stake}
                      onChange={(e) => setStake(e.target.value)}
                      placeholder="0.00"
                      className="log-bet-input"
                      required
                    />
                  </div>

                  <div className="log-bet-form-group">
                    <label htmlFor="log-bet-odds-mobile">Odds *</label>
                    <input
                      type="text"
                      id="log-bet-odds-mobile"
                      value={odds}
                      onChange={(e) => setOdds(e.target.value)}
                      placeholder="0.00"
                      className="log-bet-input"
                      required
                    />
                  </div>

                  {/* Row 2: Potential Profit | Bookmaker */}
                  <div className="log-bet-form-group">
                    <label>Potential Profit ({getCurrencySymbol(currency)})</label>
                    <div className="log-bet-profit-display">
                      {getCurrencySymbol(currency)}{calculatePotentialProfit().toFixed(2)}
                    </div>
                  </div>

                  <div className="log-bet-form-group">
                    <label htmlFor="log-bet-bookmaker-mobile">Bookmaker *</label>
                    <select
                      id="log-bet-bookmaker-mobile"
                      value={bookmaker}
                      onChange={(e) => setBookmaker(e.target.value)}
                      className="log-bet-input log-bet-select"
                      required
                    >
                      <option value="">Select a bookmaker</option>
                      <optgroup label="UK">
                        {bookmakers.UK.map(bm => (
                          <option key={bm} value={bm}>{bm}</option>
                        ))}
                      </optgroup>
                      <optgroup label="US">
                        {bookmakers.US.map(bm => (
                          <option key={bm} value={bm}>{bm}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Australia">
                        {bookmakers.Australia.map(bm => (
                          <option key={bm} value={bm}>{bm}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Canada">
                        {bookmakers.Canada.map(bm => (
                          <option key={bm} value={bm}>{bm}</option>
                        ))}
                      </optgroup>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Row 3: Notes (full width) */}
                  <div className="log-bet-form-group log-bet-form-group-full">
                    <label htmlFor="log-bet-notes-mobile">Notes (Optional)</label>
                    <textarea
                      id="log-bet-notes-mobile"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes about this bet..."
                      rows={3}
                      className="log-bet-input log-bet-textarea"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="log-bet-modal-actions">
                {isUpdateMode ? (
                  <>
                    <button
                      onClick={handleLogBetClose}
                      className="log-bet-modal-button log-bet-modal-button-cancel"
                      disabled={updating || deleting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="log-bet-modal-button log-bet-modal-button-delete"
                      disabled={updating || deleting}
                    >
                      Delete Bet
                    </button>
                    <button
                      onClick={handleLogBetSubmit}
                      className="log-bet-modal-button log-bet-modal-button-submit"
                      disabled={updating || deleting || !stake || !odds || !bookmaker || parseFloat(stake) <= 0 || parseFloat(odds) <= 0}
                    >
                      {updating ? 'Updating...' : 'Update Bet'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleLogBetClose}
                      className="log-bet-modal-button log-bet-modal-button-cancel"
                      disabled={logging}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLogBetSubmit}
                      className="log-bet-modal-button log-bet-modal-button-submit"
                      disabled={logging || !stake || !odds || !bookmaker || parseFloat(stake) <= 0 || parseFloat(odds) <= 0}
                    >
                      {logging ? 'Logging...' : 'Log Bet'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Confirmation Modal */}
        {showConfirmation && createPortal(
          <div className="log-bet-modal-overlay" onClick={handleLogBetClose}>
            <div className="log-bet-modal log-bet-confirmation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="log-bet-modal-header">
                <h3 className="log-bet-confirmation-title">Bet Logged</h3>
              </div>
              <div className="log-bet-modal-body">
                <p className="log-bet-confirmation-message">
                  Your bet has been {lastActionWasUpdate ? 'updated' : 'logged'} successfully.
                </p>
              </div>
              <div className="log-bet-modal-actions">
                <button
                  onClick={handleLogBetClose}
                  className="log-bet-modal-button log-bet-modal-button-submit"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && createPortal(
          <div className="log-bet-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="log-bet-modal log-bet-confirmation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="log-bet-modal-header">
                <h3 className="log-bet-confirmation-title">Delete Bet?</h3>
              </div>
              <div className="log-bet-modal-body">
                <p className="log-bet-confirmation-message">
                  Are you sure you want to delete this bet? This action cannot be undone.
                </p>
                {error && (
                  <div className="log-bet-error-message">
                    {error}
                  </div>
                )}
              </div>
              <div className="log-bet-modal-actions">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="log-bet-modal-button log-bet-modal-button-cancel"
                  disabled={deleting}
                >
                  No
                </button>
                <button
                  onClick={handleDeleteBet}
                  className="log-bet-modal-button log-bet-modal-button-delete"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Yes'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Betfair Bet Modal - Mobile */}
        {showBetfairBetModal && createPortal(
          <div 
            className="log-bet-modal-overlay" 
            onClick={handleBetfairBetClose}
            onTouchStart={(e) => {
              // Prevent immediate close on mobile touch
              if (e.target === e.currentTarget) {
                e.preventDefault();
              }
            }}
          >
            <div className="log-bet-modal" onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
              {/* Race Information Header */}
              <div className="log-bet-modal-header">
                <div className="log-bet-race-info">
                  <div className="log-bet-race-time-course">
                    {formatTime(race.race_time)} {race.course}
                  </div>
                  <div className="log-bet-race-specs">
                    {formatRaceSpecs(race)}
                  </div>
                </div>
              </div>

              {/* Selection Badge */}
              <div className="log-bet-selection-badge-container">
                <div className="log-bet-selection-badge">
                  {race.ai_selection.replace(/\s*\([^)]*\)\s*$/, '').trim()}
                </div>
              </div>

              {/* Divider */}
              <div className="log-bet-divider"></div>

              {/* Bet Details */}
              <div className="log-bet-modal-body">
                {betfairError && (
                  <div className="log-bet-error-message">
                    {betfairError}
                  </div>
                )}

                {loadingBetfairOdds && !isViewingBetfairBet && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#cbd5e1' }}>
                    Loading Betfair odds...
                  </div>
                )}

                {(!loadingBetfairOdds || isViewingBetfairBet) && (
                  <div className="log-bet-form-grid">
                    {/* Row 1: Stake | Odds */}
                    <div className="log-bet-form-group">
                      <label htmlFor="betfair-bet-stake-mobile">Stake ({getCurrencySymbol(currency)}) *</label>
                      <input
                        type="text"
                        id="betfair-bet-stake-mobile"
                        value={betfairStake}
                        onChange={(e) => !isViewingBetfairBet && setBetfairStake(e.target.value)}
                        placeholder="0.00"
                        className="log-bet-input"
                        required
                        disabled={isViewingBetfairBet || loadingBetfairOdds}
                        readOnly={isViewingBetfairBet}
                      />
                    </div>

                    <div className="log-bet-form-group">
                      <label htmlFor="betfair-bet-odds-mobile">Odds *</label>
                      <input
                        type="text"
                        id="betfair-bet-odds-mobile"
                        value={betfairOdds}
                        onChange={(e) => !isViewingBetfairBet && setBetfairOdds(e.target.value)}
                        placeholder="0.00"
                        className="log-bet-input"
                        required
                        disabled={isViewingBetfairBet || loadingBetfairOdds}
                        readOnly={isViewingBetfairBet}
                      />
                    </div>

                    {/* Row 2: Potential Profit | Bookmaker */}
                    <div className="log-bet-form-group">
                      <label>Potential Profit ({getCurrencySymbol(currency)})</label>
                      <div className="log-bet-profit-display">
                        {getCurrencySymbol(currency)}{calculateBetfairPotentialProfit().toFixed(2)}
                      </div>
                    </div>

                    <div className="log-bet-form-group">
                      <label htmlFor="betfair-bet-bookmaker-mobile">Bookmaker</label>
                      <input
                        type="text"
                        id="betfair-bet-bookmaker-mobile"
                        value="Betfair Exchange"
                        className="log-bet-input"
                        disabled
                        style={{ opacity: 0.7, cursor: 'not-allowed' }}
                      />
                    </div>

                    {/* Row 3: Notes (full width) */}
                    <div className="log-bet-form-group log-bet-form-group-full">
                      <label htmlFor="betfair-bet-notes-mobile">Notes (Optional)</label>
                      <textarea
                        id="betfair-bet-notes-mobile"
                        value={betfairNotes}
                        onChange={(e) => !isViewingBetfairBet && setBetfairNotes(e.target.value)}
                        placeholder="Add any notes about this bet..."
                        rows={3}
                        className="log-bet-input log-bet-textarea"
                        disabled={isViewingBetfairBet}
                        readOnly={isViewingBetfairBet}
                      />
                    </div>

                    {/* Row 4: Betfair Bet ID | Placed At (only when viewing) */}
                    {isViewingBetfairBet && existingBet && (
                      <>
                        <div className="log-bet-form-group">
                          <label>Betfair Bet ID</label>
                          <div className="log-bet-profit-display log-bet-betfair-detail" style={{ textAlign: 'center', padding: '0.75rem 1rem', background: '#0a1628', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#ffffff', fontWeight: 'normal' }}>
                            {existingBet.betfair_bet_id}
                          </div>
                        </div>

                        <div className="log-bet-form-group">
                          <label>Placed At</label>
                          <div className="log-bet-profit-display log-bet-betfair-detail" style={{ textAlign: 'center', padding: '0.75rem 1rem', background: '#0a1628', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#ffffff', fontWeight: 'normal' }}>
                            {new Date(existingBet.placed_at).toLocaleString()}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="log-bet-modal-actions">
                <button
                  onClick={handleBetfairBetClose}
                  className="log-bet-modal-button log-bet-modal-button-cancel"
                  disabled={placingBetfairBet}
                >
                  {isViewingBetfairBet ? 'Close' : 'Cancel'}
                </button>
                {!isViewingBetfairBet && (
                  <button
                    onClick={handleBetfairBetSubmit}
                    className="log-bet-modal-button log-bet-modal-button-submit"
                    disabled={placingBetfairBet || !betfairStake || !betfairOdds || parseFloat(betfairStake) <= 0 || parseFloat(betfairOdds) <= 0 || loadingBetfairOdds}
                  >
                    {placingBetfairBet 
                      ? (betfairRetryAttempt > 0 
                          ? `Attempt ${betfairRetryAttempt} of 3...` 
                          : 'Placing...')
                      : 'Place Bet on Betfair'}
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Desktop layout (existing)
  return (
    <div className="race-card">
      {/* Top Row: Course, Result Icon, Runners Badge | Time, Date, Race Info | Collapse Button */}
      <div className="race-header-full">
              <div className="race-header-left">
                <span className={`result-indicator ${getResultStatus(race.race_result, race.ai_selection, race.non_runner).toLowerCase()}`}>
                  {getResultIcon(race.race_result, race.ai_selection, race.non_runner)}
                </span>
                <h3 className="course-name">{race.course}</h3>
                <div className="field-size-badge">
                  {race.field_size} runners
                </div>
                <div className="ai-selection-badge" data-tooltip={favouriteLabel || undefined}>
                  {isAISelectionFavourite && (
                    <span
                      className="favourite-indicator"
                      aria-label={favouriteLabel || undefined}
                    >
                      <Crown size={14} />
                    </span>
                  )}
                  <span
                    className="selection-text"
                  >
                    {normalizedAISelection}
                  </span>
                </div>
              </div>
        <div className="race-header-right">
          <div className="race-time-date-specs">
            <span className="race-time">{formatTime(race.race_time)}</span>
            <span className="race-date">{formatDate(race.race_date)}</span>
            <span className="race-specs-inline">
              {formatRaceSpecs(race)}
            </span>
          </div>
          <button 
            className="collapse-button"
            onClick={handleToggleCollapse}
            title={isCollapsed ? "Expand race details" : "Collapse race details"}
          >
            {isCollapsed ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <>
          {/* Single Table Section */}
          <div className="race-data-table">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Result</th>
                  <th>Selection</th>
                  <th>Confidence</th>
                  <th>Odds</th>
                  <th>Stake (pts)</th>
                  <th>Overall</th>
                  <th>Horse</th>
                  <th>Jockey</th>
                  <th>Trainer</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={`data-cell ${getResultStatus(race.race_result, race.ai_selection, race.non_runner).toLowerCase()}`}>
                    {getResultStatus(race.race_result, race.ai_selection, race.non_runner)}
                  </td>
                  <td className="data-cell">
                    {race.ai_selection.replace(/\s*\([^)]*\)\s*$/, '').trim()}
                  </td>
                  <td 
                    className="data-cell"
                    style={{ color: getConfidenceColor(race.ai_confidence) }}
                  >
                    {race.ai_confidence.toUpperCase()}
                  </td>
                  <td className="data-cell">{formatOdds(parseFloat(race.bet365_decimal_odds || '0'))}</td>
                  <td className="data-cell">{race.suggested_stake || 'N/A'}</td>
                  <td className="data-cell score-cell">
                    <CircularProgress 
                      score={race.ai_overall_score} 
                      label="" 
                      size={50}
                      strokeWidth={4}
                    />
                  </td>
                  <td className="data-cell score-cell">
                    <CircularProgress 
                      score={race.ai_horse_score} 
                      label="" 
                      size={50}
                      strokeWidth={4}
                    />
                  </td>
                  <td className="data-cell score-cell">
                    <CircularProgress 
                      score={race.ai_jockey_score} 
                      label="" 
                      size={50}
                      strokeWidth={4}
                    />
                  </td>
                  <td className="data-cell score-cell">
                    <CircularProgress 
                      score={race.ai_trainer_score} 
                      label="" 
                      size={50}
                      strokeWidth={4}
                    />
                  </td>
                  <td className="data-cell score-cell">
                    <CircularProgress 
                      score={race.ai_owner_score} 
                      label="" 
                      size={50}
                      strokeWidth={4}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="ai-analysis">
            <h4 className="ai-analysis-title">AI Analysis</h4>
            <p>{race.ai_analysis}</p>
          </div>

          <button 
            className="runners-toggle"
            onClick={handleToggleRunners}
          >
            {showRunners ? 'Hide Runners' : `View Runners (${raceRunners.length})`}
            <span className="runners-toggle-icon">
              {showRunners ? '−' : '+'}
            </span>
          </button>
          
          {showRunners && (
            <RunnerComparisonTable runners={raceRunners} aiSelection={race.ai_selection} />
          )}

          {/* Bet Buttons - Desktop Expanded */}
          <div className="race-bet-buttons">
            {!hasBetfairBet && (
            <button 
                type="button"
              className="race-bet-button log-bet-button"
              onClick={handleLogBetClick}
                onTouchStart={handleDisabledButtonTap}
                disabled={raceHasStarted}
                title={raceHasStarted ? 'Cannot place bets as the race has begun' : ''}
            >
              {existingBet ? 'Update Bet' : 'Log Bet'}
            </button>
            )}
            {!(raceHasStarted && hasBetfairBet) && (
              <button 
                type="button"
                className="race-bet-button place-betfair-button"
                onClick={handleBetfairBetClick}
                onTouchStart={handleDisabledButtonTap}
                disabled={raceHasStarted && !hasBetfairBet}
                title={raceHasStarted && !hasBetfairBet ? 'Cannot place bets as the race has begun' : ''}
              >
                {hasBetfairBet ? 'View Bet Details' : 'Place Bet on Betfair'}
            </button>
            )}
          </div>
        </>
      )}

      {/* Bet Buttons - Desktop Collapsed */}
      {isCollapsed && (
        <div className="race-bet-buttons">
          {!hasBetfairBet && (
          <button 
              type="button"
            className="race-bet-button log-bet-button"
            onClick={handleLogBetClick}
              onTouchStart={handleDisabledButtonTap}
              disabled={raceHasStarted}
              title={raceHasStarted ? 'Cannot place bets as the race has begun' : ''}
          >
            {existingBet ? 'Update Bet' : 'Log Bet'}
          </button>
          )}
          {!(raceHasStarted && hasBetfairBet) && (
            <button 
              type="button"
              className="race-bet-button place-betfair-button"
              onClick={handleBetfairBetClick}
              onTouchStart={handleDisabledButtonTap}
              disabled={raceHasStarted && !hasBetfairBet}
              title={raceHasStarted && !hasBetfairBet ? 'Cannot place bets as the race has begun' : ''}
            >
              {hasBetfairBet ? 'View Bet Details' : 'Place Bet on Betfair'}
          </button>
          )}
        </div>
      )}

      {/* Log Bet Modal - Using Portal to render outside card */}
      {showLogBetModal && createPortal(
        <div className="log-bet-modal-overlay" onClick={handleLogBetClose}>
          <div className="log-bet-modal" onClick={(e) => e.stopPropagation()}>
            {/* Race Information Header */}
            <div className="log-bet-modal-header">
              <div className="log-bet-race-info">
                <div className="log-bet-race-time-course">
                  {formatTime(race.race_time)} {race.course}
                </div>
                <div className="log-bet-race-specs">
                  {formatRaceSpecs(race)}
                </div>
              </div>
            </div>

            {/* Selection Badge */}
            <div className="log-bet-selection-badge-container">
              <div className="log-bet-selection-badge">
                {race.ai_selection.replace(/\s*\([^)]*\)\s*$/, '').trim()}
              </div>
            </div>

            {/* Divider */}
            <div className="log-bet-divider"></div>

            {/* Bet Details */}
            <div className="log-bet-modal-body">
              {error && (
                <div className="log-bet-error-message">
                  {error}
                </div>
              )}

              <div className="log-bet-form-grid">
                {/* Row 1: Stake | Odds */}
                <div className="log-bet-form-group">
                  <label htmlFor="log-bet-stake">Stake ({getCurrencySymbol(currency)}) *</label>
                  <input
                    type="text"
                    id="log-bet-stake"
                    value={stake}
                    onChange={(e) => setStake(e.target.value)}
                    placeholder="0.00"
                    className="log-bet-input"
                    required
                  />
                </div>

                <div className="log-bet-form-group">
                  <label htmlFor="log-bet-odds">Odds *</label>
                  <input
                    type="text"
                    id="log-bet-odds"
                    value={odds}
                    onChange={(e) => setOdds(e.target.value)}
                    placeholder="0.00"
                    className="log-bet-input"
                    required
                  />
                </div>

                {/* Row 2: Potential Profit | Bookmaker */}
                <div className="log-bet-form-group">
                  <label>Potential Profit ({getCurrencySymbol(currency)})</label>
                  <div className="log-bet-profit-display">
                    {getCurrencySymbol(currency)}{calculatePotentialProfit().toFixed(2)}
                  </div>
                </div>

                <div className="log-bet-form-group">
                  <label htmlFor="log-bet-bookmaker">Bookmaker *</label>
                  <select
                    id="log-bet-bookmaker"
                    value={bookmaker}
                    onChange={(e) => setBookmaker(e.target.value)}
                    className="log-bet-input log-bet-select"
                    required
                  >
                    <option value="">Select a bookmaker</option>
                    <optgroup label="UK">
                      {bookmakers.UK.map(bm => (
                        <option key={bm} value={bm}>{bm}</option>
                      ))}
                    </optgroup>
                    <optgroup label="US">
                      {bookmakers.US.map(bm => (
                        <option key={bm} value={bm}>{bm}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Australia">
                      {bookmakers.Australia.map(bm => (
                        <option key={bm} value={bm}>{bm}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Canada">
                      {bookmakers.Canada.map(bm => (
                        <option key={bm} value={bm}>{bm}</option>
                      ))}
                    </optgroup>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Row 3: Notes (full width) */}
                <div className="log-bet-form-group log-bet-form-group-full">
                  <label htmlFor="log-bet-notes">Notes (Optional)</label>
                  <textarea
                    id="log-bet-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes about this bet..."
                    rows={3}
                    className="log-bet-input log-bet-textarea"
                  />
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="log-bet-modal-actions">
              {isUpdateMode ? (
                <>
                  <button
                    onClick={handleLogBetClose}
                    className="log-bet-modal-button log-bet-modal-button-cancel"
                    disabled={updating || deleting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="log-bet-modal-button log-bet-modal-button-delete"
                    disabled={updating || deleting}
                  >
                    Delete Bet
                  </button>
                  <button
                    onClick={handleLogBetSubmit}
                    className="log-bet-modal-button log-bet-modal-button-submit"
                    disabled={updating || deleting || !stake || !odds || !bookmaker || parseFloat(stake) <= 0 || parseFloat(odds) <= 0}
                  >
                    {updating ? 'Updating...' : 'Update Bet'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleLogBetClose}
                    className="log-bet-modal-button log-bet-modal-button-cancel"
                    disabled={logging}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogBetSubmit}
                    className="log-bet-modal-button log-bet-modal-button-submit"
                    disabled={logging || !stake || !odds || !bookmaker || parseFloat(stake) <= 0 || parseFloat(odds) <= 0}
                  >
                    {logging ? 'Logging...' : 'Log Bet'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmation Modal - Desktop */}
      {showConfirmation && createPortal(
        <div className="log-bet-modal-overlay" onClick={handleLogBetClose}>
          <div className="log-bet-modal log-bet-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="log-bet-modal-header">
              <h3 className="log-bet-confirmation-title">Bet Logged</h3>
            </div>
            <div className="log-bet-modal-body">
              <p className="log-bet-confirmation-message">
                Your bet has been {isUpdateMode ? 'updated' : 'logged'} successfully.
              </p>
            </div>
            <div className="log-bet-modal-actions">
              <button
                onClick={handleLogBetClose}
                className="log-bet-modal-button log-bet-modal-button-submit"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal - Desktop */}
      {showDeleteConfirm && createPortal(
        <div className="log-bet-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="log-bet-modal log-bet-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="log-bet-modal-header">
              <h3 className="log-bet-confirmation-title">Delete Bet?</h3>
            </div>
            <div className="log-bet-modal-body">
              <p className="log-bet-confirmation-message">
                Are you sure you want to delete this bet? This action cannot be undone.
              </p>
              {error && (
                <div className="log-bet-error-message">
                  {error}
                </div>
              )}
            </div>
            <div className="log-bet-modal-actions">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="log-bet-modal-button log-bet-modal-button-cancel"
                disabled={deleting}
              >
                No
              </button>
              <button
                onClick={handleDeleteBet}
                className="log-bet-modal-button log-bet-modal-button-delete"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Betfair Bet Modal */}
      {showBetfairBetModal && createPortal(
        <div 
          className="log-bet-modal-overlay" 
          onClick={handleBetfairBetClose}
          onTouchStart={(e) => {
            // Prevent immediate close on mobile touch
            if (e.target === e.currentTarget) {
              e.preventDefault();
            }
          }}
        >
          <div className="log-bet-modal" onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
            {/* Race Information Header */}
            <div className="log-bet-modal-header">
              <div className="log-bet-race-info">
                <div className="log-bet-race-time-course">
                  {formatTime(race.race_time)} {race.course}
                </div>
                <div className="log-bet-race-specs">
                  {formatRaceSpecs(race)}
                </div>
              </div>
            </div>

            {/* Selection Badge */}
            <div className="log-bet-selection-badge-container">
              <div className="log-bet-selection-badge">
                {race.ai_selection.replace(/\s*\([^)]*\)\s*$/, '').trim()}
              </div>
            </div>

            {/* Divider */}
            <div className="log-bet-divider"></div>

            {/* Bet Details */}
            <div className="log-bet-modal-body">
              {betfairError && (
                <div className="log-bet-error-message">
                  {betfairError}
                </div>
              )}

              {loadingBetfairOdds && !isViewingBetfairBet && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#cbd5e1' }}>
                  Loading Betfair odds...
                </div>
              )}

              {(!loadingBetfairOdds || isViewingBetfairBet) && (
                <div className="log-bet-form-grid">
                  {/* Row 1: Stake | Odds */}
                  <div className="log-bet-form-group">
                    <label htmlFor="betfair-bet-stake">Stake ({getCurrencySymbol(currency)}) *</label>
                    <input
                      type="text"
                      id="betfair-bet-stake"
                      value={betfairStake}
                      onChange={(e) => !isViewingBetfairBet && setBetfairStake(e.target.value)}
                      placeholder="0.00"
                      className="log-bet-input"
                      required
                      disabled={isViewingBetfairBet || loadingBetfairOdds}
                      readOnly={isViewingBetfairBet}
                    />
                  </div>

                  <div className="log-bet-form-group">
                    <label htmlFor="betfair-bet-odds">Odds *</label>
                    <input
                      type="text"
                      id="betfair-bet-odds"
                      value={betfairOdds}
                      onChange={(e) => !isViewingBetfairBet && setBetfairOdds(e.target.value)}
                      placeholder="0.00"
                      className="log-bet-input"
                      required
                      disabled={isViewingBetfairBet || loadingBetfairOdds}
                      readOnly={isViewingBetfairBet}
                    />
                  </div>

                  {/* Row 2: Potential Profit | Bookmaker */}
                  <div className="log-bet-form-group">
                    <label>Potential Profit ({getCurrencySymbol(currency)})</label>
                    <div className="log-bet-profit-display">
                      {getCurrencySymbol(currency)}{calculateBetfairPotentialProfit().toFixed(2)}
                    </div>
                  </div>

                  <div className="log-bet-form-group">
                    <label htmlFor="betfair-bet-bookmaker">Bookmaker</label>
                    <input
                      type="text"
                      id="betfair-bet-bookmaker"
                      value="Betfair Exchange"
                      className="log-bet-input"
                      disabled
                      style={{ opacity: 0.7, cursor: 'not-allowed' }}
                    />
                  </div>

                  {/* Row 3: Notes (full width) */}
                  <div className="log-bet-form-group log-bet-form-group-full">
                    <label htmlFor="betfair-bet-notes">Notes (Optional)</label>
                    <textarea
                      id="betfair-bet-notes"
                      value={betfairNotes}
                      onChange={(e) => !isViewingBetfairBet && setBetfairNotes(e.target.value)}
                      placeholder="Add any notes about this bet..."
                      rows={3}
                      className="log-bet-input log-bet-textarea"
                      disabled={isViewingBetfairBet}
                      readOnly={isViewingBetfairBet}
                    />
                  </div>

                  {/* Row 4: Betfair Bet ID | Placed At (only when viewing) */}
                  {isViewingBetfairBet && existingBet && (
                    <>
                      <div className="log-bet-form-group">
                        <label>Betfair Bet ID</label>
                        <div className="log-bet-profit-display log-bet-betfair-detail" style={{ textAlign: 'center', padding: '0.75rem 1rem', background: '#0a1628', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#ffffff', fontWeight: 'normal' }}>
                          {existingBet.betfair_bet_id}
                        </div>
                      </div>

                      <div className="log-bet-form-group">
                        <label>Placed At</label>
                        <div className="log-bet-profit-display log-bet-betfair-detail" style={{ textAlign: 'center', padding: '0.75rem 1rem', background: '#0a1628', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#ffffff', fontWeight: 'normal' }}>
                          {new Date(existingBet.placed_at).toLocaleString()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="log-bet-modal-actions">
              <button
                onClick={handleBetfairBetClose}
                className="log-bet-modal-button log-bet-modal-button-cancel"
                disabled={placingBetfairBet}
              >
                {isViewingBetfairBet ? 'Close' : 'Cancel'}
              </button>
              {!isViewingBetfairBet && (
                <button
                  onClick={handleBetfairBetSubmit}
                  className="log-bet-modal-button log-bet-modal-button-submit"
                  disabled={placingBetfairBet || !betfairStake || !betfairOdds || parseFloat(betfairStake) <= 0 || parseFloat(betfairOdds) <= 0 || loadingBetfairOdds}
                >
                  {placingBetfairBet 
                    ? (betfairRetryAttempt > 0 
                        ? `Attempt ${betfairRetryAttempt} of 3...` 
                        : 'Placing...')
                    : 'Place Bet on Betfair'}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RaceCard;
